import { useEffect, useRef } from "react";
import { intl } from "LocaleWrapper";
import { Tag, TagSize } from "@cofano/cck";
import { Box, Stack, Typography } from "@mui/material";
import ForumIcon from "@mui/icons-material/Forum";
import { FormattedDate } from "utils/DateUtils";
import { CommentableEntity } from "../../utils";

const CommentsWidgetList = ({ entity }: { entity: CommentableEntity }) => {

	const messagesEndRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
	}, [ entity.comments.length ]);

	if (entity.comments.length === 0) {
		return (
			<Box
				sx={{
					alignItems: "center",
					color: "text.disabled",
					display: "flex",
					flexDirection: "column",
					justifyContent: "center",
					height: "100%",
					gap: 1.5,
					py: 8,
				}}>
				<ForumIcon sx={{ fontSize: 40, color: "action.disabled" }} />
				<Typography variant="body2">{intl.formatMessage({ id: "NO_COMMENTS" })}</Typography>
			</Box>
		);
	}

	return (
		<Stack spacing={2}>
			{entity.comments.map(c => (
				<Box
					key={c.id}
					sx={{
						backgroundColor: "background.paper",
						border: "1px solid",
						borderColor: "divider",
						display: "flex",
						flexDirection: "column",
						p: 1.25,
					}}>
					<Box
						sx={{
							alignItems: "center",
							display: "flex",
							mb: 0.5,
						}}>
						<Box
							sx={{
								alignItems: "center",
								display: "flex",
								flexGrow: 1,
								overflow: "hidden",
								mr: 1,
							}}>
							<Typography
								sx={{
									color: "text.primary",
									fontSize: 12,
									fontWeight: 500,
									textOverflow: "ellipsis",
									overflow: "hidden",
									whiteSpace: "nowrap",
								}}>
								{c.createdBy.displayName}
							</Typography>

							{c.label && (
								<Box
									sx={{
										borderLeft: "1px solid",
										borderColor: "divider",
										display: "inline-flex",
										marginLeft: 1,
										paddingLeft: 1,
									}}>
									<Tag text={c.label.text} color={c.label.color} size={TagSize.SMALL} />
								</Box>
							)}
						</Box>

						<Typography sx={{ fontSize: 11, fontWeight: 500, color: "text.secondary", textAlign: "right", minWidth: 70 }}>
							<FormattedDate date={c.created} withoutYear />
						</Typography>
					</Box>

					<Typography
						sx={{
							fontSize: 13,
							color: "text.primary",
							fontWeight: 400,
							wordBreak: "break-word",
							whiteSpace: "pre-wrap",
							lineHeight: 1.4,
						}}>
						{c.comment}
					</Typography>
				</Box>
			))}

			<div ref={messagesEndRef} />
		</Stack>
	);
};

export default CommentsWidgetList;
